// Left rail for the Freehand workspace: tool picker, snap & drawing helpers,
// reference photo. Panel-level settings (symmetry, ball opacity, grid) live
// in the Right Inspector.

import React from 'react';
import { useFreehand } from '@/lib/freehandStore';
import { PenLine, MousePointerClick, Eraser, Move3D, Undo2, Redo2, Trash2, Info, Magnet, ImageIcon, X, Crosshair, Shapes, Square, RectangleHorizontal, Triangle, Pentagon, Hexagon, Diamond, Circle, Scissors, Eye, EyeOff } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { FreehandSvgImport } from './FreehandSvgImport';
import { RectangleSizeInputs } from './RectangleSizeInputs';

const TOOLS = [
  { id: 'freeform', label: 'Freeform', hint: 'Click-drag to paint a closed loop', Icon: PenLine },
  { id: 'polyline', label: 'Polyline', hint: 'Click to add corners, double-click to close', Icon: MousePointerClick },
  { id: 'shape',    label: 'Shape',    hint: 'Drag from centre to stamp a starter polygon', Icon: Shapes },
  { id: 'edit',     label: 'Edit',     hint: 'Reshape a panel: drag corners, double-click to round',   Icon: Move3D },
  { id: 'erase',    label: 'Erase panel', hint: 'Click a committed panel to delete it', Icon: Eraser },
];

const SHAPE_KINDS = [
  { id: 'triangle', label: 'Triangle', Icon: Triangle },
  { id: 'square',   label: 'Square',   Icon: Square },
  { id: 'rectangle', label: 'Rectangle', Icon: RectangleHorizontal },
  { id: 'diamond',  label: 'Diamond',  Icon: Diamond },
  { id: 'pentagon', label: 'Pentagon', Icon: Pentagon },
  { id: 'hexagon',  label: 'Hexagon',  Icon: Hexagon },
  { id: 'circle',   label: 'Circle',   Icon: Circle },
];

export default function FreehandLeftRail() {
  const tool = useFreehand((s) => s.tool);
  const setTool = useFreehand((s) => s.setTool);
  const shapeKind = useFreehand((s) => s.shapeKind);
  const setShapeKind = useFreehand((s) => s.setShapeKind);
  const undo = useFreehand((s) => s.undo);
  const redo = useFreehand((s) => s.redo);
  const clearAll = useFreehand((s) => s.clearAll);
  const snapEnabled = useFreehand((s) => s.snapEnabled);
  const setSnapEnabled = useFreehand((s) => s.setSnapEnabled);
  const partitionMode = useFreehand((s) => s.partitionMode);
  const setPartitionMode = useFreehand((s) => s.setPartitionMode);
  const punchThrough = useFreehand((s) => s.punchThrough);
  const setPunchThrough = useFreehand((s) => s.setPunchThrough);
  const spherized = useFreehand((s) => s.spherized);
  const setSpherized = useFreehand((s) => s.setSpherized);
  const showGuides = useFreehand((s) => s.showGuides);
  const setShowGuides = useFreehand((s) => s.setShowGuides);
  const referenceImage = useFreehand((s) => s.referenceImage);
  const setReferenceImage = useFreehand((s) => s.setReferenceImage);
  const setReferenceRotationY = useFreehand((s) => s.setReferenceRotationY);
  const setReferenceScaleX = useFreehand((s) => s.setReferenceScaleX);
  const setReferenceScaleY = useFreehand((s) => s.setReferenceScaleY);
  const clearReferenceImage = useFreehand((s) => s.clearReferenceImage);
  const referenceHidden = useFreehand((s) => s.referenceHidden);
  const setReferenceHidden = useFreehand((s) => s.setReferenceHidden);
  const fileInputRef = React.useRef(null);
  // Downscale user-uploaded reference photos before storing them. Mongo's
  // per-document BSON limit is 16 MB, and a full-resolution phone photo
  // (~6 MB raw ⇒ ~8 MB base64) can easily blow through it when combined
  // with the panel payload and thumbnail. 1600 px on the long side is
  // ample for tracing on a sphere and keeps the encoded string < ~500 KB.
  const downscaleToDataUrl = (file, maxDim = 1600, quality = 0.85) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = (ev) => {
        const img = new Image();
        img.onerror = reject;
        img.onload = () => {
          const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
          const w = Math.round(img.width * scale);
          const h = Math.round(img.height * scale);
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          // JPEG keeps photos small; PNG for tiny images (< 512 px longest)
          // to preserve any diagrams / crisp edges.
          const mime = Math.max(w, h) < 512 ? 'image/png' : 'image/jpeg';
          resolve(canvas.toDataURL(mime, quality));
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    });
  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      const dataUrl = await downscaleToDataUrl(f);
      setReferenceImage(dataUrl);
    } catch (err) {
      // Fallback to raw upload if the browser can't decode the image (e.g.
      // exotic format) — the save might still fail if it's huge, but the
      // preview will at least render.
      const reader = new FileReader();
      reader.onload = (ev) => setReferenceImage(ev.target.result);
      reader.readAsDataURL(f);
    }
  };
  const historyLen = useFreehand((s) => s.history.length);
  const futureLen = useFreehand((s) => s.future.length);
  const panelCount = useFreehand((s) => s.panels.length);
  const pending = useFreehand((s) => s.pendingPolyline);
  const polylineCommit = useFreehand((s) => s.polylineCommit);
  const polylineUndoPoint = useFreehand((s) => s.polylineUndoPoint);
  const polylineCancel = useFreehand((s) => s.polylineCancel);

  return (
    <div className="w-full h-full min-h-0 flex flex-col overflow-y-auto overscroll-contain text-slate-900 freehand-rail-scroll [&>*]:shrink-0" data-testid="freehand-left-rail">
      <div className="px-4 pt-4 pb-2 border-b border-slate-200">
        <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">Freehand</div>
        <div className="text-sm font-semibold">Draw panels</div>
      </div>

      <div className="p-3 space-y-2 border-b border-slate-200">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Tool</div>
        {TOOLS.map(({ id, label, hint, Icon }) => (
          <button
            key={id}
            onClick={() => setTool(id)}
            data-testid={`freehand-tool-${id}`}
            className={`w-full text-left rounded-xl px-3 py-2 border transition-colors flex items-start gap-3 ${
              tool === id ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            <Icon className="w-4 h-4 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{label}</div>
              <div className={`text-[11px] ${tool === id ? 'text-slate-200' : 'text-slate-500'} leading-snug`}>{hint}</div>
            </div>
          </button>
        ))}
        {tool === 'shape' && (
          <div className="pt-2 mt-1 border-t border-slate-200 space-y-1.5" data-testid="freehand-shape-picker">
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Starter shape</div>
            <div className="grid grid-cols-3 gap-1.5">
              {SHAPE_KINDS.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  onClick={() => setShapeKind(id)}
                  data-testid={`freehand-shape-${id}`}
                  title={label}
                  className={`rounded-lg py-2 border transition-colors flex flex-col items-center gap-0.5 ${
                    shapeKind === id
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-[10px] leading-none">{label}</span>
                </button>
              ))}
            </div>
            {shapeKind === 'rectangle' && <RectangleSizeInputs />}
            <div className="text-[11px] text-slate-500 leading-snug bg-slate-50 border border-slate-200 rounded-lg p-2">
              Click and drag on the ball — the shape scales from where you first pressed.
            </div>
          </div>
        )}
      </div>

      <div className="p-3 border-b border-slate-200 space-y-1.5">
        <TooltipProvider delayDuration={700}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setShowGuides(!showGuides)}
                data-testid="freehand-guides-toggle"
                className={`w-full rounded-lg px-2.5 py-1.5 border transition-colors flex items-center gap-2 ${
                  showGuides ? 'border-pink-500 bg-pink-500 text-white' : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'
                }`}
              >
                <Crosshair className="w-3.5 h-3.5 shrink-0" />
                <span className="text-xs font-medium flex-1 text-left">Guide lines</span>
                <span className={`w-1.5 h-1.5 rounded-full ${showGuides ? 'bg-white' : 'bg-slate-300'}`} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-[220px] bg-slate-900 text-white text-[11px] leading-snug">
              Bright pink meridian &amp; parallel that follow the pointer so you can line up points around the ball.
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setSnapEnabled(!snapEnabled)}
                data-testid="freehand-snap-toggle"
                className={`w-full rounded-lg px-2.5 py-1.5 border transition-colors flex items-center gap-2 ${
                  snapEnabled ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'
                }`}
              >
                <Magnet className="w-3.5 h-3.5 shrink-0" />
                <span className="text-xs font-medium flex-1 text-left">Snap to edges</span>
                <span className={`w-1.5 h-1.5 rounded-full ${snapEnabled ? 'bg-white' : 'bg-slate-300'}`} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-[220px] bg-slate-900 text-white text-[11px] leading-snug">
              Locks clicks &amp; ends of strokes onto nearby corners or edges of existing panels.
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setPartitionMode(!partitionMode)}
                data-testid="freehand-partition-toggle"
                className={`w-full rounded-lg px-2.5 py-1.5 border transition-colors flex items-center gap-2 ${
                  partitionMode ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'
                }`}
              >
                <span className="w-3.5 h-3.5 rounded-full border-2 border-current shrink-0" />
                <span className="text-xs font-medium flex-1 text-left">Partition mode</span>
                <span className={`w-1.5 h-1.5 rounded-full ${partitionMode ? 'bg-white' : 'bg-slate-300'}`} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-[220px] bg-slate-900 text-white text-[11px] leading-snug">
              Every point of a new stroke snaps to the nearest existing edge — great for tiling connected panels.
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setPunchThrough(!punchThrough)}
                data-testid="freehand-punch-toggle"
                className={`w-full rounded-lg px-2.5 py-1.5 border transition-colors flex items-center gap-2 ${
                  punchThrough ? 'border-amber-600 bg-amber-600 text-white' : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'
                }`}
              >
                <Scissors className="w-3.5 h-3.5 shrink-0" />
                <span className="text-xs font-medium flex-1 text-left">Punch through</span>
                <span className={`w-1.5 h-1.5 rounded-full ${punchThrough ? 'bg-white' : 'bg-slate-300'}`} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-[220px] bg-slate-900 text-white text-[11px] leading-snug">
              Every new panel cuts a matching hole out of whatever's beneath it — great for inlays and appliqués.
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setSpherized(!spherized)}
                data-testid="freehand-spherize-toggle"
                className={`w-full rounded-lg px-2.5 py-1.5 border transition-colors flex items-center gap-2 ${
                  spherized ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'
                }`}
              >
                <span className={`w-3.5 h-3.5 rounded-full shrink-0 ${spherized ? 'bg-white/25' : 'bg-slate-200'}`} />
                <span className="text-xs font-medium flex-1 text-left">Spherize panels</span>
                <span className={`w-1.5 h-1.5 rounded-full ${spherized ? 'bg-white' : 'bg-slate-300'}`} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-[220px] bg-slate-900 text-white text-[11px] leading-snug">
              Curves each panel to hug the ball. Turn off for a faceted, low-poly look.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <FreehandSvgImport />

      <div className="p-3 border-b border-slate-200 space-y-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-400">Reference photo</div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFile}
          className="hidden"
          data-testid="freehand-reference-input"
        />
        {referenceImage ? (
          <>
            <div className="flex gap-1.5">
              <button
                onClick={() => fileInputRef.current?.click()}
                data-testid="freehand-reference-replace"
                className="flex-1 rounded-lg text-xs font-medium py-2 bg-white border border-slate-200 hover:border-slate-300 flex items-center justify-center gap-1.5"
              >
                <ImageIcon className="w-3.5 h-3.5" /> Replace
              </button>
              <button
                onClick={() => setReferenceHidden(!referenceHidden)}
                data-testid="freehand-reference-toggle"
                className={`rounded-lg px-2 py-2 border ${referenceHidden ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'}`}
                title={referenceHidden ? 'Show reference photo' : 'Hide reference photo'}
              >
                {referenceHidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={clearReferenceImage}
                data-testid="freehand-reference-clear"
                className="rounded-lg px-2 py-2 bg-white border border-red-200 text-red-600 hover:bg-red-50"
                title="Remove reference photo"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="text-[11px] text-slate-500 flex items-center gap-2">
              <span>Rotate</span>
              <input
                type="range"
                min={-Math.PI}
                max={Math.PI}
                step={Math.PI / 90}
                value={referenceImage.rotationY || 0}
                onChange={(e) => setReferenceRotationY(Number(e.target.value))}
                className="flex-1"
                data-testid="freehand-reference-rotate"
              />
            </div>
            <div className="text-[11px] text-slate-500 flex items-center gap-2">
              <span className="w-9">Scale&nbsp;X</span>
              <input
                type="range"
                min={0.25}
                max={4}
                step={0.05}
                value={referenceImage.scaleX ?? 1}
                onChange={(e) => setReferenceScaleX(Number(e.target.value))}
                className="flex-1"
                data-testid="freehand-reference-scale-x"
              />
              <span className="w-8 tabular-nums text-right">{(referenceImage.scaleX ?? 1).toFixed(2)}×</span>
            </div>
            <div className="text-[11px] text-slate-500 flex items-center gap-2">
              <span className="w-9">Scale&nbsp;Y</span>
              <input
                type="range"
                min={0.25}
                max={4}
                step={0.05}
                value={referenceImage.scaleY ?? 1}
                onChange={(e) => setReferenceScaleY(Number(e.target.value))}
                className="flex-1"
                data-testid="freehand-reference-scale-y"
              />
              <span className="w-8 tabular-nums text-right">{(referenceImage.scaleY ?? 1).toFixed(2)}×</span>
            </div>
          </>
        ) : (
          <button
            onClick={() => fileInputRef.current?.click()}
            data-testid="freehand-reference-upload"
            className="w-full rounded-lg text-xs font-medium py-2 bg-white border border-slate-200 hover:border-slate-300 flex items-center justify-center gap-1.5"
          >
            <ImageIcon className="w-3.5 h-3.5" /> Upload image to trace
          </button>
        )}
      </div>

      {pending && (
        <div className="p-3 border-b border-slate-200 space-y-2 bg-amber-50">
          <div className="text-[11px] text-amber-800 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Polyline in progress · {pending.points.length} points · double-click to close</span>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            <button
              onClick={polylineUndoPoint}
              data-testid="freehand-poly-undo"
              className="rounded-lg text-xs font-medium py-1.5 bg-white border border-slate-200 hover:border-slate-300"
            >
              Undo pt
            </button>
            <button
              onClick={polylineCommit}
              disabled={pending.points.length < 3}
              data-testid="freehand-poly-commit"
              className="rounded-lg text-xs font-semibold py-1.5 bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40"
            >
              Close
            </button>
            <button
              onClick={polylineCancel}
              data-testid="freehand-poly-cancel"
              className="rounded-lg text-xs font-medium py-1.5 bg-white border border-slate-200 hover:border-slate-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="p-3 mt-auto space-y-2 border-t border-slate-200">
        <div className="flex items-center gap-1.5">
          <button
            onClick={undo}
            disabled={!historyLen}
            data-testid="freehand-undo"
            className="flex-1 rounded-lg text-xs font-medium py-1.5 bg-white border border-slate-200 hover:border-slate-300 disabled:opacity-40 flex items-center justify-center gap-1"
          >
            <Undo2 className="w-3.5 h-3.5" /> Undo
          </button>
          <button
            onClick={redo}
            disabled={!futureLen}
            data-testid="freehand-redo"
            className="flex-1 rounded-lg text-xs font-medium py-1.5 bg-white border border-slate-200 hover:border-slate-300 disabled:opacity-40 flex items-center justify-center gap-1"
          >
            <Redo2 className="w-3.5 h-3.5" /> Redo
          </button>
        </div>
        <button
          onClick={clearAll}
          data-testid="freehand-clear"
          className="w-full rounded-lg text-xs font-medium py-1.5 bg-white border border-red-200 text-red-600 hover:bg-red-50 flex items-center justify-center gap-1.5"
        >
          <Trash2 className="w-3.5 h-3.5" /> Clear all panels
        </button>
        <div className="text-[11px] text-slate-500 text-center font-mono">
          {panelCount} panel{panelCount === 1 ? '' : 's'} committed
        </div>
      </div>
    </div>
  );
}
